import axios from 'axios';
import { isStopped } from './stoppableCalls';
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';
import { createDocEntryStore } from './docStore';
import { readState, writeState } from './docState';
import { loadPdf, renderPdfPageBand } from './pdfRender';
import { buildStepPartsPrompt } from './stepPartsPrompt';
import { storedId } from './partsCatalogue';
import { readStepInstructions } from './stepInstructionsRun';
import { STEP_PARTS_MODEL } from './models';
import type { StepBand } from './categoryBands';
import type { PartItem } from './partsRun';
import { haltGate } from './runHalt';
import { asPicture, withDoc } from './promptPictures';
import type { TipPicture } from './PromptBadge';

// The REPARTITION: which supplied parts each assembly step adds, and how many — Phase 2's
// second answer, after the parts list itself.
//
// It used to be the first thing Phase 4 asked, once per step, from that step's diagram
// alone. But "step 3 adds two of part D" is not a fact about step 3: the manual supplies
// four, so it is equally a claim about every other step, and a reader shown one diagram
// can only guess at the rest. Twelve independent guesses do not add up to a supplied
// quantity except by luck, which is what the reconciliation table kept reporting.
//
// So it is asked ONCE, of a reader holding the supplied quantities and every step's
// diagram at the same time — the only position from which a quantity can be shared out.
// Phase 4 is then told what its step adds, and spends its call on what a single diagram
// can actually answer: the tools, the sub-steps, the gestures.

// A part a step adds, and how many. `id` is the part's index in the Phase 2 parts list (as
// a string); `qty` is null when the model couldn't tell. The step's total and the authority —
// the table adds it up, the sub-steps partition it.
//
// It carried how the total was reached for a while — `per` × `times` identical units, and what
// one unit was — which nothing but a small "2 × 2" under the number ever read. Dropped from the
// question and from here: a step's total is the answer.
export type StepPart = {
  id: string;
  qty: number | null;
};
// step category ("Assemblage - Etape N") -> the parts that step adds.
export type ByStepParts = Record<string, StepPart[]>;

// What a step ACHIEVES, in one line per language — the reading the allocation was made
// from, said out loud.
//
// It comes free with the repartition: to decide that step 3 takes the seat, the model has
// to have understood that step 3 sits the seat on the frame, and that understanding was
// being thrown away. Kept, it does two things. It shows a reader WHY a row of the table
// says what it says, which no quantity can. And it is the one piece of context the
// per-step call cannot get for itself: this reader saw all four diagrams in sequence, and
// that one sees one.
//
// Deliberately at the level of what the step accomplishes, never how — see the prompt. The
// gestures are Phase 4's question, asked of a closer look at a bigger image, and a
// description that scripted them would answer it in advance from a worse seat.
export type StepText = { en: string; fr: string };
export type ByStepText = Record<string, StepText>;

// AND WHICH TOOLS EACH STEP CALLS FOR — step category -> part ids of the tools.
//
// Phase 4's question until now, asked once per step of a call that also had to break the
// step into sub-steps. A tool is not consumed, so it is not part of the repartition; it is
// here because it is read off the same diagrams by the same reader, and because this reader
// is the only one who sees every step at once — "the screwdriver is used in four of these"
// is a thing you can only notice from here.
export type ByStepTools = Record<string, string[]>;

type Snapshot = {
  busy: boolean;
  byStep: ByStepParts;
  text: ByStepText;
  tools: ByStepTools;
  // The model's reasoning for the whole repartition (one call, one reasoning).
  reasoning: string;
  // Its raw reply, shown under the reasoning like Phase 2's per-page badges.
  raw: string;
  error: string | null;
};

const partsKey = (docId: string) => `notice.partsSteps.${docId}`;
const textKey = (docId: string) => `notice.partsStepsText.${docId}`;
const toolsKey = (docId: string) => `notice.partsStepsTools.${docId}`;
const reasonKey = (docId: string) => `notice.partsStepsReason.${docId}`;
const rawKey = (docId: string) => `notice.partsStepsRaw.${docId}`;
// Where the same answer lived when Phase 4 was the one asking. Read as a fallback so a
// manual analysed before today opens with its repartition intact instead of blank — it is
// the same map, under the same keys, decided by a worse question.
const legacyKey = (docId: string) => `notice.steps.used.${docId}`;

function loadByStep(docId: string): ByStepParts {
  const stored = readState<ByStepParts | null>(docId, partsKey(docId), null);
  const map = stored ?? readState<ByStepParts>(docId, legacyKey(docId), {});
  return map && typeof map === 'object' ? map : {};
}

const store = createDocEntryStore<Snapshot>((docId) => ({
  snapshot: {
    busy: false,
    byStep: loadByStep(docId),
    // Absent on every manual allocated before the descriptions were asked for — which is
    // not "this step does nothing", so it is an empty map and the UI simply has nothing
    // to print.
    text: readState<ByStepText>(docId, textKey(docId), {}),
    // Empty on every manual allocated before the tools were asked for here — which is not
    // "this step needs none", so the reader falls back to Phase 4's stored answer.
    tools: readState<ByStepTools>(docId, toolsKey(docId), {}),
    reasoning: readState<string>(docId, reasonKey(docId), ''),
    raw: readState<string>(docId, rawKey(docId), ''),
    error: null,
  },
}));

const update = store.patch;

// How wide a step band is rendered for the repartition. Narrower than the per-step call
// (1100), because this one carries EVERY band in a single body and nginx stops at 25m —
// and what it has to read is which callout letters appear in which diagram, not the fine
// print of one.
const BAND_WIDTH = 900;
// Bands per call. A backstop against a 60-step manual bursting the body, not a policy:
// the whole point of the call is that it sees every step.
const MAX_BANDS = 32;

// Share the supplied parts out among the assembly steps: ONE Gemini call carrying every
// step's diagram bands and the supplied-parts catalogue. Replaces the previous answer
// whole — a repartition is one answer about one manual, and half of one means nothing.
export async function extractStepParts(
  docId: string,
  file: Blob,
  steps: StepBand[],
  items: PartItem[],
  model: string,
  t: (k: string) => string,
) {
  if (store.getEntry(docId).snapshot.busy) return;
  if (!model || steps.length === 0 || items.length === 0) {
    update(docId, { error: t('notice.cat2.noRun') });
    return;
  }

  const halted = haltGate(docId);
  update(docId, { busy: true, error: null });

  let loadingTask: PDFDocumentLoadingTask | null = null;
  try {
    const buf = await file.arrayBuffer();
    loadingTask = loadPdf(buf);
    const doc: PDFDocumentProxy = await loadingTask.promise;

    // Every step's bands, in printed order, each tagged with its step number so the
    // server can caption it and the model can answer per step.
    const images: { step: number; image: string }[] = [];
    for (const step of steps) {
      for (const page of step.pages) {
        if (halted()) break;
        if (images.length >= MAX_BANDS) break;
        const band = step.bands[page] ?? { top: 0, bottom: 1 };
        const image = await renderPdfPageBand(doc, page, band.top, band.bottom, BAND_WIDTH);
        if (image) images.push({ step: step.num, image });
      }
    }
    // Rendering is local and free; stopping here is what keeps the call from being sent,
    // which is the only saving a halt can make.
    if (halted()) return;
    if (!images.length) {
      update(docId, { error: t('notice.cat2.noRun') });
      return;
    }
    // Said out loud, like the parts context's own cap: a repartition that read only the
    // first steps is still a complete-looking table.
    const shown = new Set(images.map((i) => i.step));
    if (shown.size < steps.length) {
      // eslint-disable-next-line no-console
      console.info(
        `[notice] repartition: steps ${[...shown].join(', ')} of ${steps.length} ` +
          `(${MAX_BANDS} diagram bands max; the rest are not sent)`,
      );
    }

    const { data } = await axios.post<{
      steps: {
        step: number; parts: StepPart[]; textEn?: string; textFr?: string; tools?: string[];
      }[];
      reasoning?: string;
      raw?: string;
    }>('/api/notice/step-parts', { model, images, prompt: buildStepPartsPrompt(items) });

    // Keyed by category, like everything downstream: a step number that names no step of
    // this manual is dropped rather than stored under a category nothing will read.
    const byCategory = new Map(steps.map((s) => [s.num, s.category]));
    const byStep: ByStepParts = {};
    const text: ByStepText = {};
    const tools: ByStepTools = {};
    for (const s of data.steps || []) {
      const category = byCategory.get(s.step);
      if (!category) continue;
      // Ids come back one higher than they are stored — see shownId, and stepsRun, which
      // undoes the same offset on the same kind of answer.
      byStep[category] = (s.parts || []).map((u) => ({ ...u, id: storedId(u.id, items.length) }));
      tools[category] = (s.tools || []).map((id) => storedId(id, items.length));
      // Only when there is something to say. An empty description is not a description,
      // and storing "" would print an empty line under every step row.
      if (s.textEn || s.textFr) text[category] = { en: s.textEn || '', fr: s.textFr || '' };
    }
    // Every step gets an entry, including one the model left out: an absent step is
    // "not answered" everywhere else in this app, and here the answer IS that it adds
    // nothing. Without this a step the model skipped would look unanalysed forever.
    // Descriptions get no such filling-in: silence about what a step does is silence.
    for (const s of steps) if (!(s.category in byStep)) byStep[s.category] = [];

    const reasoning = data.reasoning || '';
    const raw = data.raw || '';
    writeState(docId, partsKey(docId), byStep);
    writeState(docId, textKey(docId), text);
    writeState(docId, toolsKey(docId), tools);
    writeState(docId, reasonKey(docId), reasoning);
    writeState(docId, rawKey(docId), raw);
    update(docId, { byStep, text, tools, reasoning, raw });
  } catch (e) {
    const msg =
      (axios.isAxiosError(e) && (e.response?.data as { error?: string })?.error) ||
      (e instanceof Error && e.message) ||
      t('notice.err.generic');
    // A call Stop cancelled is not a failure: the table keeps what it had.
    if (!isStopped(e)) update(docId, { error: msg });
  } finally {
    update(docId, { busy: false });
    void loadingTask?.destroy();
  }
}

// THE WHOLE OF PHASE 3, from either place that starts it: the run-all chain and the panel's
// own button. Two calls with one question between them — what a step is made of and what it
// prints — and they were sequenced once here rather than twice, because the two call sites
// have already disagreed about this phase once.
//
// SIDE BY SIDE, and both awaited: neither reads the other's answer, so waiting for the
// repartition before transcribing was a wait for nothing. The reading needs no parts list, so
// it goes ahead where there is none — a manual whose parts were never extracted still gets its
// text. On STEP_PARTS_MODEL, both.
export async function runStepPartsPhase(
  docId: string,
  file: Blob,
  steps: StepBand[],
  items: PartItem[],
  t: (k: string) => string,
) {
  if (steps.length === 0) return;
  await Promise.all([
    items.length > 0 ? extractStepParts(docId, file, steps, items, STEP_PARTS_MODEL, t) : null,
    readStepInstructions(docId, file, steps, STEP_PARTS_MODEL, t),
  ]);
}

// THE DIAGRAMS THIS CALL CARRIES, for the badge beside the button.
//
// The prompt was the only half of it on screen, and the whole argument for asking the
// repartition ONCE is that one reader sees every step's diagram at the same time. Whether
// that is what actually goes up — every step, in printed order, cut to the band Phase 1
// filed — is a fact about the images and about nothing else.
//
// Rendered here by the same renderPdfPageBand at the same BAND_WIDTH, under the same
// MAX_BANDS cap, in the same loop the call uses: so a manual whose last steps are being
// dropped by that cap shows it here, before the call is paid for.
export async function stepPartsPictures(
  file: Blob,
  steps: StepBand[],
  t: (k: string) => string,
): Promise<TipPicture[]> {
  return withDoc(file, async (doc) => {
    const out: TipPicture[] = [];
    const seen = new Map<number, number>();
    for (const step of steps) {
      for (const page of step.pages) {
        if (out.length >= MAX_BANDS) return out;
        const band = step.bands[page] ?? { top: 0, bottom: 1 };
        const image = await renderPdfPageBand(doc, page, band.top, band.bottom, BAND_WIDTH);
        if (!image) continue;
        const nth = (seen.get(step.num) ?? 0) + 1;
        seen.set(step.num, nth);
        // The label is what the CALL labels this image with — the server captions each band
        // "ASSEMBLY STEP n, diagram k", and that caption is the only thing telling one step's
        // drawing from the next one's. The page it was cut from is the second line.
        out.push(asPicture(
          image,
          t('notice.parts.promptStepBand')
            .replace('{n}', String(step.num))
            .replace('{k}', String(nth)),
          `${t('notice.pdf.page')} ${page}`,
        ));
      }
    }
    return out;
  }, []);
}

export const useStepParts = store.useSnapshot;
export const getStepParts = (docId: string): ByStepParts => store.get(docId).byStep;
// What each step adds and achieves, outside a component — the run-all chain reads it to
// hand the step analysis its context, and a chain cannot call a hook.
export const getStepText = (docId: string): ByStepText => store.get(docId).text;
// And which tools each step calls for, for the readers that have a category and no hook.
export const getStepTools = (docId: string): ByStepTools => store.get(docId).tools;
